import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import {
  Radar,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
  Loader2,
  FlaskConical,
  Pencil,
  Trash2,
  ShieldCheck,
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import type { Organization } from '@/services/organizations.service';
import { useTranslation } from 'react-i18next';

interface OrganizationDataTableProps {
  organizations: Organization[];
  isLoading?: boolean;
  testingIds?: Set<string>;
  onTest: (organization: Organization) => void;
  onEdit: (organization: Organization) => void;
  onDelete: (organizationId: string) => void;
  isDeletePending?: boolean;
}

type SortKey = 'name' | 'domain' | 'supportEmail' | 'status';

type SortConfig = {
  key: SortKey;
  direction: 'asc' | 'desc';
};

const STATUS_FILTERS = [
  { labelKey: 'common:state.allStatus', label: 'Alle Status', value: 'all' },
  { labelKey: 'common:state.active', label: 'Aktiv', value: 'active' },
  { labelKey: 'common:state.inactive', label: 'Inaktiv', value: 'inactive' },
] as const;

const STATUS_META = {
  active: {
    labelKey: 'common:state.active', label: 'Aktiv',
    badge: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    dot: 'bg-emerald-500',
  },
  inactive: {
    labelKey: 'common:state.inactive', label: 'Inaktiv',
    badge: 'bg-slate-100 text-slate-600 border border-slate-200',
    dot: 'bg-slate-400',
  },
} as const;

const sortIcon = (active: boolean, direction: 'asc' | 'desc') => {
  if (!active) {
    return <ChevronsUpDown className="h-4 w-4 text-slate-400" />;
  }

  return direction === 'asc'
    ? <ChevronUp className="h-4 w-4 text-slate-600" />
    : <ChevronDown className="h-4 w-4 text-slate-600" />;
};

const normalizeDomain = (domain?: string | null) => {
  if (!domain) return '';
  return domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

export const OrganizationDataTable = ({
  organizations,
  isLoading,
  testingIds,
  onTest,
  onEdit,
  onDelete,
  isDeletePending,
}: OrganizationDataTableProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]['value']>('all');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });

  const testingLookup = useMemo(() => testingIds ?? new Set<string>(), [testingIds]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return organizations.filter((org) => {
      const status = org.is_active ? 'active' : 'inactive';
      const matchesStatus = statusFilter === 'all' || status === statusFilter;

      const tokens = [org.name, org.support_email, normalizeDomain(org.domain)]
        .filter(Boolean)
        .map((token) => token!.toLowerCase());
      const matchesSearch = term ? tokens.some((token) => token.includes(term)) : true;

      return matchesStatus && matchesSearch;
    });
  }, [organizations, search, statusFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;

    list.sort((a, b) => {
      switch (sortConfig.key) {
        case 'domain':
          return normalizeDomain(a.domain).localeCompare(normalizeDomain(b.domain)) * dir;
        case 'supportEmail':
          return (a.support_email || '').localeCompare(b.support_email || '') * dir;
        case 'status':
          return Number(!!a.is_active) - Number(!!b.is_active) * dir;
        case 'name':
        default:
          return (a.name || '').localeCompare(b.name || '') * dir;
      }
    });

    return list;
  }, [filtered, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const rows = sorted.slice(startIndex, startIndex + pageSize);

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'name' ? 'asc' : 'desc' },
    );
  };

  const handlePrevious = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setPage((prev) => Math.max(1, prev - 1));
  };

  const handleNext = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setPage((prev) => Math.min(totalPages, prev + 1));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span className="text-sm">{t('settings:tbl.loadingOrgs', 'Organisationen werden geladen...')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('settings:tbl.searchOrg', 'Nach Name, Domain oder E-Mail suchen')}
            className="h-9 pl-9 border-slate-200 bg-white focus-visible:ring-slate-200"
          />
          <Radar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}
        >
          <SelectTrigger className="h-9 w-full sm:w-48 border-slate-200 bg-white focus:ring-slate-200">
            <SelectValue placeholder={t('common:field.status', 'Status')} />
          </SelectTrigger>
          <SelectContent align="end">
            {STATUS_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.labelKey ? t(option.labelKey, option.label) : option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow className="hover:bg-slate-50 border-b border-slate-100">
              <TableHead className="min-w-[220px] py-3.5">
                <Button
                  variant="ghost"
                  className="-ml-3 h-auto px-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                  onClick={() => handleSort('name')}
                >
                  <span className="flex items-center gap-2">
                    {t('common:field.organization', 'Organisation')}
                    {sortIcon(sortConfig.key === 'name', sortConfig.direction)}
                  </span>
                </Button>
              </TableHead>
              <TableHead className="min-w-[200px] py-3.5">
                <Button
                  variant="ghost"
                  className="-ml-3 h-auto px-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                  onClick={() => handleSort('domain')}
                >
                  <span className="flex items-center gap-2">
                    {t('common:field.domain', 'Domain')}
                    {sortIcon(sortConfig.key === 'domain', sortConfig.direction)}
                  </span>
                </Button>
              </TableHead>
              <TableHead className="min-w-[220px] py-3.5">
                <Button
                  variant="ghost"
                  className="-ml-3 h-auto px-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                  onClick={() => handleSort('supportEmail')}
                >
                  <span className="flex items-center gap-2">
                    {t('settings:tbl.supportEmail', 'Support-E-Mail')}
                    {sortIcon(sortConfig.key === 'supportEmail', sortConfig.direction)}
                  </span>
                </Button>
              </TableHead>
              <TableHead className="min-w-[140px] py-3.5">
                <Button
                  variant="ghost"
                  className="-ml-3 h-auto px-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                  onClick={() => handleSort('status')}
                >
                  <span className="flex items-center gap-2">
                    {t('common:field.status', 'Status')}
                    {sortIcon(sortConfig.key === 'status', sortConfig.direction)}
                  </span>
                </Button>
              </TableHead>
              <TableHead className="w-[150px] text-right py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {t('common:field.actions', 'Aktionen')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((org) => {
              const status = org.is_active ? 'active' : 'inactive';
              const statusInfo = STATUS_META[status];
              const isTesting = testingLookup.has(org.id);

              return (
                <TableRow key={org.id} className="group hover:bg-slate-50/50 border-b border-slate-100 last:border-0 transition-colors">
                  <TableCell className="py-4">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{org.name}</p>
                    </div>
                  </TableCell>
                  <TableCell className="py-4 text-sm text-slate-600">{normalizeDomain(org.domain) || '—'}</TableCell>
                  <TableCell className="py-4 text-sm text-slate-600">{org.support_email || '—'}</TableCell>
                  <TableCell className="py-4">
                    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium border shadow-sm', statusInfo.badge)}>
                      <span className={cn('h-1.5 w-1.5 rounded-full', statusInfo.dot)} />
                      {statusInfo.labelKey ? t(statusInfo.labelKey, statusInfo.label) : statusInfo.label}
                    </span>
                  </TableCell>
                  <TableCell className="text-right py-4">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-slate-900"
                        onClick={() => onTest(org)}
                        disabled={isTesting}
                      >
                        {isTesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200" onClick={() => onEdit(org)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                            disabled={isDeletePending}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('common:x2.deleteOrg', 'Organisation löschen')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('common:x2.areYouSureYou', 'Sind Sie sicher, dass Sie')} {org.name} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-lg">{t('common:action.cancel', 'Abbrechen')}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDelete(org.id)}
                              className="bg-red-600 hover:bg-red-700 rounded-lg"
                              disabled={isDeletePending}
                            >
                              {t('common:action.delete', 'Löschen')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                       <ShieldCheck className="h-6 w-6 text-slate-300" />
                    </div>
                    <p className="font-medium text-slate-900">{t('common:x2.noOrgsFound', 'Keine Organisationen gefunden')}</p>
                    <p className="text-sm mt-1">{t('common:x1.adjustSearchFilters', 'Passen Sie den Suchbegriff oder die Filter an.')}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {sorted.length > 0 && (
        <div className="flex flex-col gap-4 border-t border-slate-200 pt-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-muted-foreground">
            {t('common:x1.showing2', 'Zeigt')}{' '}
            <span className="font-medium text-slate-900">
              {sorted.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + pageSize, sorted.length)}
            </span>{' '}
            von{' '}
            <span className="font-medium text-slate-900">{sorted.length}</span> {t('settings:tbl.orgs', 'Organisationen')}
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {t('settings:tbl.perPage', 'Pro Seite')}
              <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number(value))}>
                <SelectTrigger className="h-8 w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 50].map((option) => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Pagination className="justify-end">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={handlePrevious}
                    className={cn(currentPage === 1 && 'pointer-events-none opacity-50')}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-muted-foreground">
                    {t('common:field.page', 'Seite')} {currentPage} von {totalPages}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={handleNext}
                    className={cn(currentPage === totalPages && 'pointer-events-none opacity-50')}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationDataTable;
